import { ComponentProps, FC } from "react";
import * as _ from "lodash";
import { useSelector } from "react-redux";

import {
  ChartFilterType,
  SectionEntity,
  Enterprise,
  EnterpriseAppState,
  DataDescriptorEntity
} from "@ctra/api";

import { Col, Row, Switch, Typography, Space } from "@ctra/components";
import { classname } from "@ctra/utils";
import { useTranslation, Enterprise as Content } from "@ctra/i18n";

import {
  DIMGroupFilter,
  DateMethod,
  HerdGroupFilter,
  ISODurationFilter,
  useChartFilters,
  PenFilter
} from "@chart";

import { useDataDictionary } from "@base";

import { testIds } from "../../../../testing";
import styles from "./SectionToolbar.module.less";

const { Text } = Typography;

interface SectionToolbarProps extends ComponentProps<typeof Row> {
  /**
   * section ID for the toolbar
   */
  sectionID?: SectionEntity["id"];
  /**
   * disabled state
   */
  disabled?: boolean;
  /**
   * projections widget settings
   */
  projections?: {
    state: boolean;
    onChange: (showProjections: boolean) => void;
  };
}

/**
 * Section filtering toolbar
 * @param {string} sectionID
 * @param {{visible: boolean, state: boolean, onChange: (showProjections: boolean) => void} | undefined} projections
 * @param {boolean | undefined} disabled
 * @param {string | undefined} className
 * @param {Omit<React.PropsWithChildren<SectionToolbarProps>, "projections" | "disabled" | "className" | "sectionID">} rest
 * @returns {JSX.Element | null}
 * @constructor
 */
export const SectionToolbar: FC<SectionToolbarProps> = ({
  sectionID,
  projections,
  disabled,
  className,
  ...rest
}) => {
  const { t } = useTranslation();

  const {
    subsets: { projectionsEnabled }
  } = useDataDictionary();

  const { filters, isoDuration, timePeriod, api, metadata } = useChartFilters();
  const { herdGroupID, dimGroupKey, penName } = filters;
  const { setFilter, setISODuration, setTimePeriod, setCurrentDateMethod } = api;
  const { filterDefinitions, supportedISODurations, showFilters, supportedfarmIDs } = metadata;

  const { state, onChange } = _.defaults(projections, {
    state: false,
    onChange: _.noop
  });

  const {
    layouts: {
      section: {
        subsection: { projections: projectionsLabel }
      }
    }
  } = Content;

  /**
   * Get a list of chart containers for the section
   * @todo get only for this section, flat
   * @type {ChartContainerList}
   */
  const dataDescriptorsOfSection = useSelector<EnterpriseAppState, Array<DataDescriptorEntity["id"]>>(
    (state) =>
      sectionID
        ? _.map(
            Enterprise.entities.getChartContainersOfSection(state, {
              sectionID
            }),
            "dataDescriptorID"
          )
        : []
  );

  /**
   * Tell if any of the charts support projections
   * @type {string[]}
   */
  const overlap = _.intersection(dataDescriptorsOfSection, projectionsEnabled);

  /**
   * Handle herd group changes
   * @param herdGroupIDList
   */
  const onHerdGroupChange: ComponentProps<typeof HerdGroupFilter>["onHerdGroupChange"] = (herdGroupIDList) =>
    setFilter(ChartFilterType.herdGroup, herdGroupIDList);

  /**
   * Handle pen changes
   * @param {Array<PenEntity["id"]>} penNameList
   */
  const onPenChange: ComponentProps<typeof PenFilter>["onPenChange"] = (penNameList) =>
    setFilter(ChartFilterType.penName, penNameList);

  /**
   * Handle DIM group changes
   * @param {Array<DIMGroupEntity["id"]>} dimGroupIDList
   */
  const onDIMGroupChange: ComponentProps<typeof DIMGroupFilter>["onDIMGroupChange"] = (dimGroupIDList) =>
    setFilter(ChartFilterType.dimGroup, dimGroupIDList);

  /**
   * Handle ISO duration changes
   * @param {<ISODuration>} duration
   */
  const onDurationChange: ComponentProps<typeof ISODurationFilter>["onDurationChange"] = (duration) => {
    setCurrentDateMethod(DateMethod.iso);
    setISODuration(duration);
  };

  /**
   * Handle custom date range changes
   * @param {ChartTimePeriod} timePeriod
   */
  const onTimePeriodChange: ComponentProps<typeof ISODurationFilter>["onTimePeriodChange"] = (timePeriod) => {
    setCurrentDateMethod(DateMethod.custom);
    setTimePeriod(timePeriod);
  };

  /**
   * Tell if a filter is multi-selectable
   * @param {ChartFilterType} filterType
   * @param {boolean} legacyCondition
   * @return {boolean}
   * @todo deprecate `legacyCondition` after v4.9.0
   */
  const isMultiSelectable = (filterType: ChartFilterType, legacyCondition: boolean) => {
    const definition = _.find(filterDefinitions, ["name", filterType]);

    return definition ? definition.isMultiSelectable : legacyCondition;
  };

  return showFilters ? (
    <Row gutter={[16, 0]} align="middle" className={classname(styles.Wrapper, className)} {...rest}>
      {!!_.size(overlap) && (
        <Col>
          <Space align="center">
            <Text>{t<string>(projectionsLabel)}</Text>
            <Switch checked={state} onChange={onChange} />
          </Space>
        </Col>
      )}
      {!!dimGroupKey && (
        <Col data-testid={testIds.sectionToolbar.dimGroupFilter}>
          <DIMGroupFilter
            disabled={disabled}
            multiple={isMultiSelectable(ChartFilterType.dimGroup, true)}
            farmIDList={supportedfarmIDs}
            selection={dimGroupKey}
            onDIMGroupChange={onDIMGroupChange}
          />
        </Col>
      )}
      {!!herdGroupID && (
        <Col data-testid={testIds.sectionToolbar.herdGroupFilter}>
          <HerdGroupFilter
            disabled={disabled}
            farmIDList={supportedfarmIDs}
            selection={herdGroupID}
            multiple={isMultiSelectable(ChartFilterType.herdGroup, true)}
            onHerdGroupChange={onHerdGroupChange}
          />
        </Col>
      )}
      {!!penName && (
        <Col data-testid={testIds.sectionToolbar.penFilter}>
          <PenFilter
            disabled={disabled}
            farmIDList={supportedfarmIDs}
            selection={penName}
            multiple={isMultiSelectable(ChartFilterType.penName, true)}
            onPenChange={onPenChange}
          />
        </Col>
      )}
      <Col data-testid={testIds.sectionToolbar.isoDurationFilter}>
        <ISODurationFilter
          disabled={disabled}
          durations={supportedISODurations}
          duration={isoDuration}
          timePeriod={timePeriod}
          onDurationChange={onDurationChange}
          onTimePeriodChange={onTimePeriodChange}
        />
      </Col>
    </Row>
  ) : null;
};
